import { BarChart } from '@ovComponents/2-component/barChart/barChart';
import {
  CHART_COLOR_BY_SUPPORT,
  getChartColors,
} from '@ovComponents/2-component/pieChart/getChartColors';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { ChartOptions, Plugin } from 'chart.js';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { isNull } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type IPendingTransactionsBar = {
  data: { key: string; value: number }[];
  view: 'type' | 'state';
  loading: boolean;
};

export const PendingTransactionsBar = ({
  data,
  view,
  loading,
}: IPendingTransactionsBar) => {
  const { t } = useTranslation(['transfer']);
  const [colors, setColors] = useState<(`#${string}` | undefined)[]>([]);
  const [activeItem, setActiveItem] = useState<string | null>(null);

  const theme = useTheme();

  useEffect(() => {
    if (view === 'type') {
      setColors([theme.color.primary, CHART_COLOR_BY_SUPPORT.supportTwo]);
    } else {
      setColors(getChartColors());
    }
  }, [view, theme]);

  const overrideBarOptions: ChartOptions<'bar'> = {
    plugins: {
      tooltip: {
        enabled: false,
        external: (externalData: any) => {
          const index = externalData.tooltip.dataPoints?.[0]?.parsed?.x;
          if (!isNull(index) && data[index]?.key !== activeItem) {
            setActiveItem(data[index]?.key);
          }
        },
      },
    },
  };

  const plugins: Plugin<'bar'>[] = [
    {
      id: 'tooltip',
      beforeEvent(_chart, args) {
        const { event } = args;
        if (event.type === 'mouseout') setActiveItem(null);
      },
    },
  ];

  return (
    <Box
      sx={{
        mt: 0.5,
      }}
    >
      {loading ? (
        <Box
          sx={{
            width: '100%',
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            {data.map((item, index) => (
              <Box
                key={item.key}
                data-testid={`pending-transactions-bar-caption-${item.key.toLowerCase()}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                  paddingY: 1.5,
                  paddingX: 1,

                  ...(activeItem === item.key
                    ? {
                        backgroundColor: theme.color.surfaceContainer,
                        borderRadius: theme.borderRadius.md,
                      }
                    : {
                        backgroundColor: 'none',
                      }),
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    bgcolor: colors[index % colors.length],
                    borderRadius: 1,
                  }}
                />
                <Typography variant='labelLarge'>
                  {view === 'type'
                    ? t(`pendingTransactionsBar.caption.${item.key}`)
                    : t(`states.${item.key}`)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              minHeight: 230,
            }}
          >
            <BarChart
              labels={data.map((item) =>
                formatMoneyValue(item.value, '$', 2, false),
              )}
              datasets={[
                {
                  label: '',
                  data: data.map((item) => item.value),
                  backgroundColor: data.map(
                    (_, index) => colors[index % colors.length],
                  ),
                  barPercentage: 1,
                  categoryPercentage: 0.98,
                },
              ]}
              format='CENTS'
              noYAxis
              xAxis={{
                color: theme.color.onSurfaceVariant,
                font: {
                  family: theme.font.base,
                  size: 12,
                  weight: '600',
                },
                borderDisplay: false,
              }}
              plugins={plugins}
              overrideOptions={overrideBarOptions}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
